import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Popover,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { CalendarToday, DoorFront } from '@mui/icons-material';
import { DateRangePicker } from 'react-date-range';
import { addDays, format } from 'date-fns';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar
} from 'recharts';
import { authenticatedFetch } from '../utils/api';

// Custom Tooltip Component
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    return (
      <Box
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          minWidth: '180px'
        }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 600, 
            color: '#333',
            mb: 1,
            borderBottom: '1px solid #eee',
            pb: 1
          }}
        >
          {data?.date}
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#f59e0b', 
            fontWeight: 500,
            mb: 0.5
          }}
        >
          Check-ins : {data?.unique_checkins?.toLocaleString()}
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#ef4444', 
            fontWeight: 500 
          }}
        >
          Check-Outs : {data?.out_premises?.toLocaleString()}
        </Typography>
      </Box>
    );
  }
  return null;
};

const PremisesStatsCard = () => {
  const today = new Date();
  const [dateRange, setDateRange] = useState([
    {
      startDate: addDays(new Date(), -7),
      endDate: new Date(),
      key: 'selection'
    }
  ]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(7);

  const buildParams = () => {
    const params = new URLSearchParams();
    params.append('start_date', format(dateRange[0].startDate, 'yyyy-MM-dd'));
    params.append('end_date', format(dateRange[0].endDate, 'yyyy-MM-dd'));
    return params.toString();
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedFetch(`/dashboard/premises-stats?${buildParams()}`);
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch premises stats:', err);
      setError(err.message || 'Unable to load premises stats');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCalendar = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleCloseCalendar = () => {
    setOpen(false);
  };

  const handleDateRangeChange = (ranges) => {
    setDateRange([ranges.selection]);
  };

  const handleApplyDateRange = () => {
    setSelectedPreset(null);
    fetchStats();
    handleCloseCalendar();
  };

  const handlePresetChange = (event, newPreset) => {
    if (newPreset !== null) {
      setSelectedPreset(newPreset);
      const newDateRange = [{
        startDate: addDays(new Date(), -newPreset),
        endDate: new Date(),
        key: 'selection'
      }];
      setDateRange(newDateRange);
    }
  };

  // Fetch when preset changes
  useEffect(() => {
    if (selectedPreset !== null) {
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPreset]);

  const filledData = useMemo(() => {
    if (!stats?.daily_counts || !stats.date_range) {
      return [];
    }
    
    return stats.daily_counts.map(item => ({
      date: item.date,
      displayDate: format(new Date(item.date), 'MMM dd'),
      unique_checkins: item.unique_checkins || 0,
      out_premises: item.out_premises || 0
    }));
  }, [stats]);

  return (
    <Card
      sx={{
        height: '100%',
        border: '1px solid rgba(64, 81, 138, 0.12)',
        borderRadius: 3,
        boxShadow: '0 10px 28px rgba(31, 45, 84, 0.12)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
      }}
    >
      <CardContent sx={{ p: { xs: 1.75, sm: 2.25, md: 2.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexWrap: 'wrap', gap: { xs: 1.5, sm: 2 }, mb: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 1.5 }, minWidth: 0 }}>
            <Box sx={{ p: { xs: 1.2, sm: 1.5 }, background: 'rgba(102, 126, 234, 0.1)', borderRadius: 2, display: 'flex', alignItems: 'center' }}>
              <DoorFront color="primary" sx={{ fontSize: { xs: 22, sm: 24 } }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1.1rem', sm: '1.25rem' }, lineHeight: 1.25 }}>
              Check-ins & Out Premises
            </Typography>
          </Box>
          <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                p: 1.5,
                bgcolor: 'rgba(64, 81, 138, 0.04)',
                width: { xs: '100%', sm: 'auto' },
                cursor: 'pointer'
              }}
              onClick={handleOpenCalendar}
            >
              <CalendarToday fontSize="small" sx={{color: '#473e8a'}} />
              <Typography variant="body2" sx={{ fontSize: { xs: '0.82rem', sm: '0.88rem' }, flex: 1 }}>
                {format(dateRange[0].startDate, 'MMM dd')} - {format(dateRange[0].endDate, 'MMM dd')}
              </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'end', width: '100%', flexDirection: 'row-reverse' }}>
            
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                px: 1.1,
                py: 0.7,
                bgcolor: 'rgba(64, 81, 138, 0.04)',
                width: { xs: '100%', sm: 'auto' }
              }}
            >
             
            <ToggleButtonGroup
                value={selectedPreset}
                exclusive
                onChange={handlePresetChange}
                size="small"
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  '& .MuiToggleButton-root': {
                    flex: { xs: 1, sm: 'initial' },
                    px: { xs: 1, sm: 1.5 },
                    py: 0.5,
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    textTransform: 'none',
                    '&.Mui-selected': {
                      backgroundColor: '#40518A',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#364775',
                      },
                    },
                  },
                  
                }}
              >
                <ToggleButton value={7}>7 Days</ToggleButton>
                <ToggleButton value={15}>15 Days</ToggleButton>
                <ToggleButton value={30}>30 Days</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            
          </Box>
        </Box>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleCloseCalendar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Box sx={{ p: 0 }}>
            <DateRangePicker
              ranges={dateRange}
              onChange={handleDateRangeChange}
              moveRangeOnFirstSelection={false}
              months={1}
              direction="horizontal"
              rangeColors={['#40518A']}
              staticRanges={[]}
              inputRanges={[]}
              maxDate={today}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', p: 2, pt: 0 }}>
              <Button onClick={handleCloseCalendar} variant="outlined" size="small">
                Cancel
              </Button>
              <Button onClick={handleApplyDateRange} variant="contained" size="small">
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && !loading && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && stats && (
          <>
            {filledData.length > 0 ? (
              <Box sx={{ width: '100%', minHeight: { xs: 300, sm: 380 }, pt: 0.75 }}>
                <ResponsiveContainer width="100%" height={380}>
                  <BarChart
                    data={filledData}
                    margin={{
                      top: 16,
                      right: 10,
                      left: -8,
                      bottom: 8
                    }}
                    barGap={0}
                    barCategoryGap="20%"
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#d9e0ee" />
                    <XAxis
                      dataKey="displayDate"
                      tick={{ fontSize: 11, fill: '#667085' }}
                      interval="preserveStartEnd"
                      minTickGap={22}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#667085' }} />
                    <Tooltip
                      cursor={{ stroke: '#000', fill: 'none', strokeWidth: 0 }}
                      content={<CustomTooltip />}
                    />
                    <Legend
                      wrapperStyle={{
                        paddingTop: '8px',
                        fontSize: '12px'
                      }}
                    />
                    <Bar
                      dataKey="unique_checkins"
                      name="Check-ins"
                      fill="#10b981"
                      radius={[4, 4, 0, 0]}
                      label={({ x, y, width, index }) => {
                        const entry = filledData[index];
                        if (!entry) return null;
                        const diff = entry.unique_checkins - entry.out_premises;
                        if (diff < 0) return null;
                        const labelText = diff > 0 ? `${diff}` : '0';
                        const labelX = diff === 0 ? x + width : x + width / 2;
                        return (
                          <text
                            x={labelX}
                            y={y - 8}
                            fill="#1e40af"
                            textAnchor="middle"
                            fontSize={11}
                            fontWeight={700}
                          >
                            {labelText}
                          </text>
                        );
                      }}
                    />
                    <Bar
                      dataKey="out_premises"
                      name="Check-outs"
                      fill="#ef4444"
                      radius={[4, 4, 0, 0]}
                      label={({ x, y, width, index }) => {
                        const entry = filledData[index];
                        if (!entry) return null;
                        const diff = entry.unique_checkins - entry.out_premises;
                        if (diff >= 0) return null;
                        return (
                          <text
                            x={x + width / 2}
                            y={y - 8}
                            fill="#ef4444"
                            textAnchor="middle"
                            fontSize={11}
                            fontWeight={700}
                          >
                            {diff}
                          </text>
                        );
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 320,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.default'
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No premises data available for the selected period.
                </Typography>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PremisesStatsCard;
